import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';

const MarketplaceComponent = ({ user }) => {
  const [tab, setTab] = useState('buy');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListing, setNewListing] = useState({ title: '', description: '', price_usd: '', price_cualcoin: '', category: 'product' });
  const [buyModal, setBuyModal] = useState(null);
  const [ratingModal, setRatingModal] = useState(null);
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  // Vendedor de Apoyo state
  const [showSupportSeller, setShowSupportSeller] = useState(false);
  const [supportSearchQuery, setSupportSearchQuery] = useState('');
  const [supportSearchResults, setSupportSearchResults] = useState([]);
  const [selectedSupport, setSelectedSupport] = useState(null);
  const [supportPct, setSupportPct] = useState('');

  useEffect(() => { fetchListings(); }, [tab]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = tab === 'my' ? '?mine=true' : '';
      const res = await axios.get(`${API}/marketplace/listings${params}`);
      setListings(res.data.listings || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const searchSupportSellers = async (query) => {
    if (query.length < 2) { setSupportSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/messaging/users/search?query=${query}`);
      setSupportSearchResults(res.data.users || []);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!newListing.title.trim() || !newListing.price_usd || !newListing.price_cualcoin) { 
      alert('Titulo y ambos precios (USD + CUALcoin) son obligatorios'); return; 
    }
    if (showSupportSeller && selectedSupport && (!supportPct || parseFloat(supportPct) < 1 || parseFloat(supportPct) > 50)) {
      alert('La comision del vendedor de apoyo debe ser entre 1% y 50%'); return;
    }
    try {
      const payload = {
        title: newListing.title,
        description: newListing.description,
        price_usd: parseFloat(newListing.price_usd),
        price_cualcoin: parseFloat(newListing.price_cualcoin),
        category: newListing.category
      };
      if (showSupportSeller && selectedSupport && supportPct) {
        payload.support_seller_email = selectedSupport.email;
        payload.support_commission_pct = parseFloat(supportPct);
      }
      await axios.post(`${API}/marketplace/listings`, payload);
      setShowCreateForm(false);
      setNewListing({ title: '', description: '', price_usd: '', price_cualcoin: '', category: 'product' });
      setShowSupportSeller(false);
      setSelectedSupport(null);
      setSupportPct('');
      setSupportSearchQuery('');
      setTab('my');
      fetchListings();
    } catch (err) { alert(err.response?.data?.detail || 'Error al crear listado'); }
  };

  const handleBuy = async (listingId, paymentMethod) => {
    setBuyModal(null);
    try {
      const res = await axios.post(`${API}/marketplace/listings/${listingId}/buy`, { payment_method: paymentMethod });
      alert(res.data.message);
      fetchListings();
    } catch (err) { alert(err.response?.data?.detail || 'Error al comprar'); }
  };

  const handleConfirm = async (listingId) => {
    if (!window.confirm('Confirmar recepcion? Se liberaran los fondos al vendedor.')) return;
    try {
      const res = await axios.post(`${API}/marketplace/listings/${listingId}/confirm`);
      alert(res.data.message);
      if (res.data.prompt_rating && res.data.escrow_id) {
        setRatingModal({ escrow_id: res.data.escrow_id });
        setRatingValue(5);
        setRatingComment('');
      }
      fetchListings();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleSubmitRating = async () => {
    if (!ratingModal) return;
    try {
      await axios.post(`${API}/reputation/rate/${ratingModal.escrow_id}`, {
        rating: ratingValue,
        comment: ratingComment
      });
      alert('Calificacion registrada!');
      setRatingModal(null);
    } catch (err) { alert(err.response?.data?.detail || 'Error al calificar'); setRatingModal(null); }
  };

  const handleDispute = async (listingId) => {
    const reason = prompt('Describe el problema con tu compra:');
    if (!reason) return;
    try {
      const res = await axios.post(`${API}/marketplace/listings/${listingId}/dispute`, { reason });
      alert(res.data.message);
      fetchListings();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCancel = async (listingId) => {
    if (!window.confirm('Cancelar este listado? Se devolvera tu garantia.')) return;
    try {
      await axios.delete(`${API}/marketplace/listings/${listingId}`);
      fetchListings();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const filtered = search ? listings.filter(l => `${l.title} ${l.description}`.toLowerCase().includes(search.toLowerCase())) : listings;

  return (
    <div className="space-y-6" data-testid="marketplace-component">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Marketplace</h2>
        <p className="text-gray-600">Mercado P2P entre usuarios de CUALAPP</p>
        <p className="text-xs text-gray-400 mt-1">Precios duales: USD + CUALcoin | Pagar con CUALcoin = menos comision</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center space-x-2">
        <button onClick={() => setTab('buy')} data-testid="tab-buy"
          className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'buy' ? 'bg-emerald-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <span className="text-lg">&#x1F402;</span><span>Comprar</span>
        </button>
        <button onClick={() => { setTab('sell'); setShowCreateForm(true); }} data-testid="tab-sell"
          className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'sell' ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <span className="text-lg">&#x1F43B;</span><span>Vender</span>
        </button>
        <button onClick={() => setTab('my')} data-testid="tab-my-listings"
          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'my' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Mis Listados
        </button>
      </div>

      {/* Search */}
      {tab === 'buy' && (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar productos o servicios..."
            data-testid="marketplace-search" className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500" />
        </div>
      )}

      {/* Create Listing Form - Dual Pricing */}
      {showCreateForm && tab === 'sell' && (
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200 p-5" data-testid="create-listing-form">
          <h3 className="font-semibold text-gray-800 mb-3">&#x1F43B; Publicar para Vender</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titulo *</label>
              <input type="text" value={newListing.title} onChange={(e) => setNewListing(p => ({ ...p, title: e.target.value }))}
                placeholder="Que vendes?" data-testid="listing-title" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
              <textarea value={newListing.description} onChange={(e) => setNewListing(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe tu producto o servicio..." data-testid="listing-description" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio USD *</label>
                <input type="number" step="0.01" value={newListing.price_usd} onChange={(e) => setNewListing(p => ({ ...p, price_usd: e.target.value }))}
                  placeholder="$0.00" data-testid="listing-price-usd" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio CUALcoin *</label>
                <input type="number" step="0.001" value={newListing.price_cualcoin} onChange={(e) => setNewListing(p => ({ ...p, price_cualcoin: e.target.value }))}
                  placeholder="0.000" data-testid="listing-price-cualcoin" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select value={newListing.category} onChange={(e) => setNewListing(p => ({ ...p, category: e.target.value }))}
                  data-testid="listing-category" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                  <option value="product">Producto</option><option value="service">Servicio</option>
                </select>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              <p>Se bloqueara <strong>{newListing.price_cualcoin || '0'} CUALCOIN</strong> como garantia (100% del precio CUALCOIN).</p>
              <p className="mt-1">La garantia se devuelve al completarse la venta o al cancelar el listado.</p>
            </div>

            {/* Vendedor de Apoyo */}
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={showSupportSeller} onChange={(e) => {
                  setShowSupportSeller(e.target.checked);
                  if (!e.target.checked) { setSelectedSupport(null); setSupportPct(''); setSupportSearchQuery(''); }
                }} data-testid="support-seller-toggle" className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500" />
                <span className="text-sm font-medium text-gray-700">Vincular Vendedor de Apoyo (referido)</span>
              </label>
              {showSupportSeller && (
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-gray-500">Al vender, un porcentaje del monto neto se pagara automaticamente al vendedor de apoyo.</p>
                  {!selectedSupport ? (
                    <div>
                      <input type="text" value={supportSearchQuery}
                        onChange={(e) => { setSupportSearchQuery(e.target.value); searchSupportSellers(e.target.value); }}
                        placeholder="Buscar por nombre o email..."
                        data-testid="support-seller-search"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                      {supportSearchResults.length > 0 && (
                        <div className="mt-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg">
                          {supportSearchResults.map(u => (
                            <button key={u.id} onClick={() => { setSelectedSupport(u); setSupportSearchResults([]); setSupportSearchQuery(''); }}
                              data-testid={`support-user-${u.id}`}
                              className="w-full flex items-center justify-between p-2 hover:bg-purple-50 text-left text-sm">
                              <span>{u.full_name} <span className="text-gray-400 text-xs">({u.email})</span></span>
                              <span className={`w-2 h-2 rounded-full ${u.is_online ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-purple-800">{selectedSupport.full_name}</p>
                        <p className="text-xs text-purple-600">{selectedSupport.email}</p>
                      </div>
                      <button onClick={() => setSelectedSupport(null)} data-testid="remove-support-seller"
                        className="text-purple-400 hover:text-purple-600 text-lg">x</button>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Comision para el apoyo (%)</label>
                    <div className="flex items-center space-x-2">
                      <input type="number" min="1" max="50" step="0.5" value={supportPct}
                        onChange={(e) => setSupportPct(e.target.value)}
                        placeholder="Ej: 10" data-testid="support-pct-input"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                      <span className="text-sm text-gray-500">% del monto neto (despues de comision CUALAPP)</span>
                    </div>
                    {supportPct && newListing.price_cualcoin && (
                      <p className="text-xs text-gray-400 mt-1">
                        Ejemplo: Si vendes por {newListing.price_cualcoin} CC → Comision CUALAPP ~2.5% → Neto ~{(parseFloat(newListing.price_cualcoin) * 0.975).toFixed(3)} CC → Apoyo recibe ~{(parseFloat(newListing.price_cualcoin) * 0.975 * parseFloat(supportPct) / 100).toFixed(3)} CC ({supportPct}%)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <button onClick={handleCreate} data-testid="publish-listing-btn"
                className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-2 px-4 rounded-lg hover:from-red-600 hover:to-orange-600 font-medium">
                Publicar
              </button>
              <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal - Payment Method Selection */}
      {buyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="buy-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Elegir metodo de pago</h3>
            <p className="text-sm text-gray-600 mb-4">Comprando: <strong>{buyModal.title}</strong></p>
            <div className="space-y-3">
              <button onClick={() => handleBuy(buyModal.id, 'CUALCOIN')} data-testid="pay-cualcoin"
                className="w-full p-4 border-2 border-emerald-300 rounded-xl hover:bg-emerald-50 transition-all text-left">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-emerald-700">Pagar con CUALCOIN</p>
                    <p className="text-xs text-emerald-600 mt-1">Comision 2.5% | Escrow 12h | +Jing</p>
                  </div>
                  <p className="text-xl font-bold text-emerald-600">{buyModal.price_cualcoin} CC</p>
                </div>
                <div className="mt-2 bg-emerald-100 rounded px-2 py-1 text-xs text-emerald-700 inline-block">Recomendado</div>
              </button>
              <button onClick={() => handleBuy(buyModal.id, 'USD')} data-testid="pay-usd"
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all text-left">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-gray-700">Pagar con USD</p>
                    <p className="text-xs text-gray-500 mt-1">Comision 5% | Escrow 48h</p>
                  </div>
                  <p className="text-xl font-bold text-gray-700">${buyModal.price_usd}</p>
                </div>
              </button>
            </div>
            <button onClick={() => setBuyModal(null)} className="w-full mt-4 py-2 text-gray-500 text-sm hover:text-gray-700">Cancelar</button>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {ratingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="rating-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Califica esta transaccion</h3>
            <p className="text-sm text-gray-600 mb-4">Tu opinion ayuda a mejorar la confianza en el marketplace</p>
            <div className="flex justify-center space-x-2 mb-4">
              {[1,2,3,4,5].map(star => (
                <button key={star} onClick={() => setRatingValue(star)} data-testid={`star-${star}`}
                  className={`text-3xl transition-transform hover:scale-110 ${star <= ratingValue ? 'text-amber-400' : 'text-gray-300'}`}>
                  &#9733;
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mb-3">{ratingValue}/5 estrellas</p>
            <textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Comentario opcional..." data-testid="rating-comment"
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none mb-4" rows={3} />
            <div className="flex space-x-3">
              <button onClick={handleSubmitRating} data-testid="submit-rating"
                className="flex-1 bg-amber-500 text-white py-2 rounded-lg font-medium hover:bg-amber-600">Enviar calificacion</button>
              <button onClick={() => setRatingModal(null)} className="px-4 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Saltar</button>
            </div>
          </div>
        </div>
      )}

      {/* Listings */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">{tab === 'buy' ? 'No hay productos disponibles' : tab === 'my' ? 'No tienes listados' : ''}</p>
          {tab === 'buy' && <p className="text-sm text-gray-400 mt-2">Se el primero en publicar algo!</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((listing) => (
            <div key={listing.id} className={`bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow ${listing.is_tienda_cualapp ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`} data-testid={`listing-${listing.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${listing.category === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {listing.category === 'product' ? 'Producto' : 'Servicio'}
                    </span>
                    {listing.is_mine && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Tuyo</span>}
                    {listing.is_tienda_cualapp && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Tienda CUALAPP</span>}
                    {listing.status && listing.status !== 'active' && <span className={`text-xs px-2 py-0.5 rounded-full ${listing.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : listing.status === 'completed' ? 'bg-green-100 text-green-700' : listing.status === 'disputed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{listing.status}</span>}
                  </div>
                  <h4 className="font-semibold text-gray-900">{listing.title}</h4>
                  {listing.description && <p className="text-sm text-gray-600 mt-1">{listing.description}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    Por: {listing.seller_name}
                    {listing.seller_rating > 0 && (
                      <span className="ml-2 text-amber-500">
                        {'★'.repeat(Math.round(listing.seller_rating))}{'☆'.repeat(5 - Math.round(listing.seller_rating))}
                        <span className="text-gray-400 ml-1">({listing.seller_total_ratings})</span>
                      </span>
                    )}
                    {listing.seller_badges?.includes('vendedor_verificado') && <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Verificado</span>}
                    {listing.seller_badges?.includes('confiable') && <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Confiable</span>}
                  </p>
                  {listing.has_support_seller && (
                    <p className="text-xs text-purple-500 mt-1" data-testid={`support-info-${listing.id}`}>
                      <span className="inline-flex items-center bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">
                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                        Apoyo: {listing.support_seller_name} ({listing.support_commission_pct}%)
                      </span>
                    </p>
                  )}
                </div>
                <div className="text-right ml-4">
                  <p className="text-lg font-bold text-emerald-600">{listing.price_cualcoin || listing.price} <span className="text-xs">CC</span></p>
                  <p className="text-sm text-gray-500">${listing.price_usd || listing.price} <span className="text-xs">USD</span></p>
                </div>
              </div>
              <div className="mt-3 flex space-x-2">
                {!listing.is_mine && listing.status === 'active' && (
                  <button onClick={() => setBuyModal(listing)} data-testid={`buy-${listing.id}`}
                    className="flex-1 bg-emerald-500 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-600">
                    &#x1F402; Comprar
                  </button>
                )}
                {listing.is_mine && listing.status === 'active' && (
                  <button onClick={() => handleCancel(listing.id)}
                    className="flex-1 bg-gray-100 text-gray-700 py-1.5 rounded-lg text-sm hover:bg-gray-200">
                    Cancelar listado
                  </button>
                )}
                {listing.buyer_id && listing.status === 'pending' && !listing.is_mine && (
                  <div className="flex space-x-2 flex-1">
                    <button onClick={() => handleConfirm(listing.id)} data-testid={`confirm-${listing.id}`}
                      className="flex-1 bg-emerald-500 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-600">
                      Confirmar recepcion
                    </button>
                    <button onClick={() => handleDispute(listing.id)} data-testid={`dispute-${listing.id}`}
                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200">
                      Disputa
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Commission Info */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 text-xs text-gray-600">
        <div className="flex justify-between">
          <div><strong>CUALCOIN:</strong> 2.5% comision | Escrow 12h | +50% Jing</div>
          <div><strong>USD:</strong> 5% comision | Escrow 48h</div>
        </div>
        <p className="mt-1 text-gray-400">Vendedores: Se requiere 100% de garantia en CUALCOIN para publicar.</p>
      </div>
    </div>
  );
};

// CUAL-TAREAS Component (Nodo 17)


export default MarketplaceComponent;
